import { SelectOption, withForm } from '@stern-ui/ui';
import { useStore } from '@tanstack/react-form';
import { add } from 'date-fns';
import { useEffect, useState } from 'react';

import { defaultSiteLanguage } from '@/globalConsts';

import { generateSurveyTimeOptions, getSurveySendStartTime } from '../../utils';

import { sendSurveyFormOptions } from './sendSurveyFormOptions';

export const SendSurveyForm = withForm({
	...sendSurveyFormOptions,
	props: {
		surveySelectOptions: [] as SelectOption[],
		isLoading: false as boolean,
	},
	render: function Render({ form, surveySelectOptions, isLoading }) {
		const isUs = defaultSiteLanguage === 'en-us';
		const startTimeLocal = getSurveySendStartTime(isUs);
		// end date is + 2 years just to minimise size of date picker
		const endDate = add(new Date(), { years: 2 });

		const generateTimeOptions = (selectedDateValue: string | undefined) =>
			generateSurveyTimeOptions(selectedDateValue, startTimeLocal, isUs);

		const selectedDateValue = useStore(form.store, (state) => state.values.send_date);
		const selectedTime = useStore(form.store, (state) => state.values.send_time);
		const [timeOptions, setTimeOptions] = useState(generateTimeOptions(selectedDateValue));

		useEffect(() => {
			// regenerate the time options based on the newly selected date
			const newTimeOptions = generateTimeOptions(selectedDateValue);
			// check time is still valid if options have changed
			const timeIsFound = newTimeOptions.find(
				(option) => option.value === selectedTime && !option.disabled
			);
			if (!timeIsFound) {
				// selected time is no longer allowed so reset
				form.setFieldValue('send_time', '', { dontUpdateMeta: true, dontValidate: true });
				// set the time field as untouched so that validation errors don't show before user has a chance to interact with the field
				form.setFieldMeta('send_time', (meta) => ({
					...meta,
					isTouched: false,
				}));
			}
			// update the select field with the new time options
			setTimeOptions(newTimeOptions);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [selectedDateValue]);

		const setDateFieldTouched = () => {
			form.setFieldMeta('send_date', (meta) => ({
				...meta,
				isTouched: true,
				isBlurred: true,
			}));
		};

		return (
			<div
				className={`w-full grid grid-cols md:grid-cols-3 gap-4 md:gap-8 mt-6 pb-5 ${isLoading ? 'opacity-0' : ''}`}
			>
				<form.AppField name='survey_version'>
					{(field) => (
						<field.SelectField label='Survey version' options={surveySelectOptions} />
					)}
				</form.AppField>
				<form.AppField name='send_date'>
					{(field) => (
						<field.CustomDateFieldV2
							label='Date to send'
							fromDate={startTimeLocal}
							toDate={endDate}
							setFieldTouched={setDateFieldTouched}
						/>
					)}
				</form.AppField>
				<form.AppField name='send_time'>
					{(field) => (
						<field.SelectField
							label='Time to send'
							options={timeOptions}
							isDisabled={!selectedDateValue}
						/>
					)}
				</form.AppField>
			</div>
		);
	},
});
